import { useState, useMemo } from "react";
import { fmt } from "@/sentinel";

// P&L-at-expiry for a vertical spread, anchored to the engine's own max_profit / max_loss
// so the curve is always consistent with the Candidates table. Read-only exploration —
// this changes nothing in the engine (no editable thresholds), it just makes the risk
// shape legible the way a static Excel table never could.
function pnlAt(c, S) {
  const kl = Math.min(c.short_strike, c.long_strike);
  const kh = Math.max(c.short_strike, c.long_strike);
  const maxP = c.max_profit;
  const maxL = -Math.abs(c.max_loss);
  const bullish = c.is_credit || c.direction === "bullish";
  let lowVal, highVal;
  if (bullish) { lowVal = maxL; highVal = maxP; }
  else { lowVal = maxP; highVal = maxL; }
  if (S <= kl) return lowVal;
  if (S >= kh) return highVal;
  const t = (S - kl) / (kh - kl || 1);
  return lowVal + t * (highVal - lowVal);
}

const usd = (v) => (v > 0 ? "+" : v < 0 ? "−" : "") + "$" + Math.abs(Math.round(v)).toLocaleString("en-US");

/** `showTitle={false}` when the diagram already sits inside a Panel titled "Payoff at expiry". */
export default function PayoffDiagram({ candidate: c, showTitle = true }) {
  const spot = c.model_params?.spot ?? c.short_strike;
  const sigma = c.model_params?.sigma ?? 20;
  const maxMove = Math.max(Math.round(3 * sigma), 30);
  const [delta, setDelta] = useState(0);

  const { W, H, x, y, path, profitArea, lossArea, breakevenX, xMin, xMax, yLo, yHi, PT, PB } = useMemo(() => {
    const kl = Math.min(c.short_strike, c.long_strike);
    const kh = Math.max(c.short_strike, c.long_strike);
    const span = Math.max(2 * sigma, (kh - kl) * 1.6, 40);
    const xMin = Math.min(kl - (kh - kl) * 0.4, spot - span);
    const xMax = Math.max(kh + (kh - kl) * 0.4, spot + span);
    const yLo = -Math.abs(c.max_loss);
    const yHi = c.max_profit;
    const pad = (yHi - yLo) * 0.12 || 50;
    const lo = yLo - pad, hi = yHi + pad;
    const W = 640, H = 220, PL = 56, PR = 14, PT = 18, PB = 34;
    const x = (s) => PL + ((s - xMin) / (xMax - xMin || 1)) * (W - PL - PR);
    const y = (v) => PT + (1 - (v - lo) / (hi - lo || 1)) * (H - PT - PB);
    const N = 60;
    const pts = [];
    for (let i = 0; i <= N; i++) {
      const s = xMin + (i / N) * (xMax - xMin);
      pts.push([x(s), y(pnlAt(c, s))]);
    }
    const line = pts.map(([px, py], i) => `${i === 0 ? "M" : "L"} ${px.toFixed(1)} ${py.toFixed(1)}`).join(" ");
    const z = y(0);
    const area = (sign) => pts.map(([px, py], i) => `${i === 0 ? "M" : "L"} ${px.toFixed(1)} ${(sign > 0 ? Math.min(py, z) : Math.max(py, z)).toFixed(1)}`).join(" ")
      + ` L ${x(xMax).toFixed(1)} ${z.toFixed(1)} L ${x(xMin).toFixed(1)} ${z.toFixed(1)} Z`;
    return { W, H, x, y, path: line, profitArea: area(1), lossArea: area(-1), breakevenX: c.breakeven, xMin, xMax, yLo, yHi, PT, PB };
  }, [c, spot, sigma]);

  const projClose = spot + delta;
  const projPnl = pnlAt(c, projClose);
  const zeroY = y(0);
  const [kLo, kHi] = [c.short_strike, c.long_strike].sort((a, b) => a - b);

  return (
    <div className="sn-stack" style={{ gap: "var(--space-3)", minWidth: 0 }} data-testid={`payoff-diagram-${c.rank}`}>
      {showTitle && <span className="sn-micro">Payoff at expiry · P&amp;L vs SPX settlement · 1 lot</span>}
      <svg className="sn-chart" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: 760 }} role="img"
        aria-label={`Payoff at expiry per lot: max profit ${usd(yHi)}, max loss ${usd(yLo)}, breakeven ${fmt(breakevenX)}, short strike ${fmt(c.short_strike, "strike")}, long strike ${fmt(c.long_strike, "strike")}`}>
        <path className="payoff-profit" d={profitArea} />
        <path className="payoff-loss" d={lossArea} />
        <line className="zero" x1={x(xMin)} x2={x(xMax)} y1={zeroY} y2={zeroY} />
        {/* strikes (labels on two rows so close strikes don't collide) */}
        {[["Short", c.short_strike, H - 18], ["Long", c.long_strike, H - 4]].map(([k, s, ly]) => (
          <g key={k}>
            <line className="strike" x1={x(s)} x2={x(s)} y1={PT} y2={H - PB} />
            <text className="strike-label" x={x(s)} y={ly} textAnchor={s === kLo ? "end" : s === kHi ? "start" : "middle"}>{k} {fmt(s, "strike")}</text>
          </g>
        ))}
        {/* breakeven */}
        <line className="open-line" x1={x(breakevenX)} x2={x(breakevenX)} y1={PT} y2={H - PB} />
        <text x={x(breakevenX)} y={PT - 6} textAnchor="middle">BE {fmt(breakevenX)}</text>
        {/* spot */}
        <line className="spot" x1={x(spot)} x2={x(spot)} y1={PT} y2={H - PB} style={{ opacity: 0.5 }} />
        {/* payoff curve */}
        <path className="payoff-line" d={path} />
        {/* scenario marker */}
        <line className="xhair" x1={x(projClose)} x2={x(projClose)} y1={PT} y2={H - PB} />
        <circle cx={x(projClose)} cy={y(projPnl)} r="4" style={{ fill: "var(--text-primary)", stroke: "var(--surface-panel)", strokeWidth: 1.5 }} />
        {/* y labels */}
        <text x={50} y={y(yHi) + 4} textAnchor="end" style={{ fill: "var(--price-up)" }}>{usd(yHi)}</text>
        <text x={50} y={y(yLo) + 4} textAnchor="end" style={{ fill: "var(--price-down)" }}>{usd(yLo)}</text>
        <text x={50} y={zeroY + 4} textAnchor="end">0</text>
      </svg>

      <div className="sn-stack" style={{ gap: "var(--space-1)" }}>
        <div className="sn-row" style={{ justifyContent: "space-between" }}>
          <label className="sn-micro" htmlFor={`payoff-slider-${c.rank}`}>Scenario · if SPX moves by the close</label>
          <span className="sn-small sn-muted">
            <span className="sn-num">{delta > 0 ? "+" : delta < 0 ? "−" : ""}{Math.abs(delta)}</span> pts → SPX <b className="sn-num" style={{ color: "var(--text-primary)", fontWeight: 500 }}>{fmt(projClose, "strike")}</b>
          </span>
        </div>
        <input
          id={`payoff-slider-${c.rank}`}
          type="range" min={-maxMove} max={maxMove} step={1} value={delta}
          onChange={(e) => setDelta(Number(e.target.value))}
          data-testid={`payoff-slider-${c.rank}`}
          style={{ width: "100%", accentColor: "var(--workspace-accent)" }}
        />
        <div className="sn-row" style={{ justifyContent: "space-between" }}>
          <span className="sn-small sn-faint">Read-only outcome explorer · engine numbers unchanged</span>
          <span className="sn-num" style={{ fontSize: 14, fontWeight: 500 }} data-testid={`payoff-pnl-${c.rank}`}>
            P&amp;L at close: {usd(projPnl)}
          </span>
        </div>
      </div>
    </div>
  );
}
